import { useState, useCallback, useEffect, useRef } from 'react';
import { useLatest } from 'ahooks';
import {
  updateShortLinkPath,
  URL_2_CHART_TYPE,
  URL_2_SORT_NAME,
  URL_2_ORDER,
  URL_2_RATIO_TYPE,
  URL_CHART_TYPE,
  URL_SORT_NAME,
  URL_ORDER,
  URL_RATIO_TYPE,
  URL_COUNTRIES_TYPE,
  URL_CHART_METRIC,
  URL_2_CHART_METRIC,
  URL_RANK_SWITCH,
} from '@intelligence/utils/urlPathCfg';
import {
  buildMetricMaxDateAvailabilityMap,
  fetchMobileStatsMetricsDateRanges,
  resolveFirstAvailableMobileStatsTabSegment,
} from '../utilsTmp/metricDate';

export function useMobileStatsInitialTabRedirect({
  gameId,
  appIds,
  isBasicLoading,
  tabType,
  activeSegment,
  isManualTabSwitch,
  onRedirect,
}: {
  gameId?: string;
  appIds?: string[];
  isBasicLoading?: boolean;
  tabType?: string;
  activeSegment?: string;
  isManualTabSwitch?: () => boolean;
  onRedirect?: (segment: string) => void;
}) {
  const hasCheckedRef = useRef(false);
  const isManualTabSwitchRef = useRef(false);
  const onRedirectRef = useRef(onRedirect);
  const isManualTabSwitchCheckerRef = useLatest(isManualTabSwitch);
  const activeSegmentRef = useRef(activeSegment);
  const [isInitialTabChecking, setIsInitialTabChecking] = useState(tabType === 'stats');

  useEffect(() => {
    onRedirectRef.current = onRedirect;
  }, [onRedirect]);

  useEffect(() => {
    activeSegmentRef.current = activeSegment;
  }, [activeSegment]);

  const markManualTabSwitch = useCallback(() => {
    isManualTabSwitchRef.current = true;
    setIsInitialTabChecking(false);
  }, []);

  useEffect(() => {
    hasCheckedRef.current = false;
    isManualTabSwitchRef.current = false;
    setIsInitialTabChecking(tabType === 'stats');
  }, [gameId, tabType]);

  useEffect(() => {
    if (tabType !== 'stats' || isBasicLoading || !gameId || !appIds?.length) {
      if (tabType !== 'stats' || !isBasicLoading) {
        setIsInitialTabChecking(false);
      }
      return;
    }

    if (hasCheckedRef.current || isManualTabSwitchRef.current || isManualTabSwitchCheckerRef.current?.()) {
      setIsInitialTabChecking(false);
      return;
    }

    let cancelled = false;

    const checkAndRedirect = async (): Promise<void> => {
      try {
        const result = await fetchMobileStatsMetricsDateRanges(
          { gameId, appIds },
        );

        if (cancelled || isManualTabSwitchRef.current || isManualTabSwitchCheckerRef.current?.()) {
          return;
        }

        hasCheckedRef.current = true;
        const firstAvailableSegment = resolveFirstAvailableMobileStatsTabSegment(
          buildMetricMaxDateAvailabilityMap(result?.data),
        );

        if (
          firstAvailableSegment &&
          firstAvailableSegment !== activeSegmentRef.current &&
          !isManualTabSwitchRef.current && !isManualTabSwitchCheckerRef.current?.()
        ) {
          onRedirectRef.current?.(firstAvailableSegment);
        }
      } catch (error) {
        console.debug(error);
      } finally {
        if (!cancelled) {
          setIsInitialTabChecking(false);
        }
      }
    };

    checkAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [appIds, gameId, isBasicLoading, tabType]);

  return {
    isInitialTabChecking,
    markManualTabSwitch,
  };
}

export function useMobileDetailShortLink(props: any) {
  const {
    shortLinkValidQuery,
    isCoutriesPage,
    // 排序指标
    trendSortMetric,
    editionSortMetric,
    countrieSortMetric,
    // 可视化类型 柱饼折
    marketChartType,
    setTrendChartType,
    trendChartType,
    editionChartType,
    setMarketChartType,
    setEditionChartType,
    // 可视化标题上的指标
    trendSegement,
    editionSegement,
    coutriesSegement,
    setTrendSegement,
    setEditionSegement,
    setCoutriesSegement,
    // coutries特有的
    coutriesType,
    trendRankSwitch,
    setTrendRankSwitch,
  } = props;
  const [trendPageSortAttr, setTrendPageSortAttr] = useState({
    currentSortValue: 'desc',
    currentSortMetric: trendSortMetric,
    currentRatioType: 0,
  });
  const [editionPageSortAttr, setEditionPageSortAttr] = useState({
    currentSortValue: 'desc',
    currentSortMetric: editionSortMetric,
    currentRatioType: 0,
  });
  const [countriesPageSortAttr, setCountriesPageSortAttr] = useState({
    currentSortValue: 'desc',
    currentSortMetric: countrieSortMetric,
    currentRatioType: 0,
  });

  const updatePageCfgFromShortLink = () => {
    if (isCoutriesPage) {
      if (shortLinkValidQuery?.[URL_CHART_TYPE] !== undefined) {
        setMarketChartType?.(shortLinkValidQuery?.[URL_CHART_TYPE]);
      }
      if (shortLinkValidQuery?.[URL_CHART_METRIC] !== undefined) {
        setCoutriesSegement?.(shortLinkValidQuery?.[URL_CHART_METRIC]);
      }
      setCountriesPageSortAttr?.({
        ...(countriesPageSortAttr || {}),
        currentSortValue: shortLinkValidQuery?.[URL_ORDER] || countriesPageSortAttr.currentSortValue,
        currentSortMetric: shortLinkValidQuery?.[URL_SORT_NAME] || countriesPageSortAttr.currentSortMetric,
        currentRatioType: shortLinkValidQuery?.[URL_RATIO_TYPE] || countriesPageSortAttr.currentRatioType,
      });

    } else {
      if (shortLinkValidQuery?.[URL_RANK_SWITCH] !== undefined) {
        setTrendRankSwitch?.(shortLinkValidQuery?.[URL_RANK_SWITCH]);
      }
      if (shortLinkValidQuery?.[URL_CHART_TYPE] !== undefined) {
        setTrendChartType?.(shortLinkValidQuery?.[URL_CHART_TYPE]);
      }
      if (shortLinkValidQuery?.[URL_2_CHART_TYPE] !== undefined) {
        setEditionChartType?.(shortLinkValidQuery?.[URL_2_CHART_TYPE]);
      }
      if (shortLinkValidQuery?.[URL_CHART_METRIC] !== undefined) {
        setTrendSegement?.(shortLinkValidQuery?.[URL_CHART_METRIC]);
      }
      if (shortLinkValidQuery?.[URL_2_CHART_METRIC] !== undefined) {
        setEditionSegement?.(shortLinkValidQuery?.[URL_2_CHART_METRIC]);
      }
      setTrendPageSortAttr?.({
        ...(trendPageSortAttr || {}),
        currentSortValue: shortLinkValidQuery?.[URL_ORDER] || trendPageSortAttr.currentSortValue,
        currentSortMetric: shortLinkValidQuery?.[URL_SORT_NAME] || trendPageSortAttr.currentSortMetric,
        currentRatioType: shortLinkValidQuery?.[URL_RATIO_TYPE] || trendPageSortAttr.currentRatioType,
      });
      setEditionPageSortAttr?.({
        ...(editionPageSortAttr || {}),
        currentSortValue: shortLinkValidQuery?.[URL_2_ORDER] || editionPageSortAttr.currentSortValue,
        currentSortMetric: shortLinkValidQuery?.[URL_2_SORT_NAME] || editionPageSortAttr.currentSortMetric,
        currentRatioType: shortLinkValidQuery?.[URL_2_RATIO_TYPE] || editionPageSortAttr.currentRatioType,
      });
    }
  };

  function filterUndefined(obj: object) {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined)
    );
  };

  const updateCountriesShortLink = () => {
    const filteredObj = filterUndefined({
      [URL_SORT_NAME]: countriesPageSortAttr.currentSortMetric,
      [URL_ORDER]: countriesPageSortAttr.currentSortValue,
      [URL_RATIO_TYPE]: countriesPageSortAttr.currentRatioType,
      [URL_CHART_TYPE]: marketChartType,
      [URL_COUNTRIES_TYPE]: coutriesType,
      [URL_CHART_METRIC]: coutriesSegement,
    });
    updateShortLinkPath({
      shortLinkQuery: filteredObj
    });
  };

  // 操作后写url
  const updateStatsShortLink = () => {
    const filteredObj = filterUndefined({
      [URL_CHART_TYPE]: trendChartType,
      [URL_SORT_NAME]: trendPageSortAttr.currentSortMetric,
      [URL_ORDER]: trendPageSortAttr.currentSortValue,
      [URL_RATIO_TYPE]: trendPageSortAttr.currentRatioType,
      [URL_CHART_METRIC]: trendSegement,
      [URL_2_SORT_NAME]: editionPageSortAttr.currentSortMetric,
      [URL_2_ORDER]: editionPageSortAttr.currentSortValue,
      [URL_2_RATIO_TYPE]: editionPageSortAttr.currentRatioType,
      [URL_2_CHART_TYPE]: editionChartType,
      [URL_2_CHART_METRIC]: editionSegement,
      [URL_RANK_SWITCH]: trendRankSwitch,
    })
    updateShortLinkPath({ shortLinkQuery: filteredObj });
  };

  // 操作后写url
  const updateMobileDetailShortLink = () => {
    if (isCoutriesPage) {
      updateCountriesShortLink();
    } else {
      updateStatsShortLink();
    }
  };

  const onTableSortChageTrend = (e: any) => {
    setTrendPageSortAttr({
      currentSortValue: e?.sortVal,
      currentSortMetric: e?.sortMetric,
      currentRatioType: e?.ratioType,
    });
    updateShortLinkPath({
      shortLinkQuery: {
        [URL_SORT_NAME]: e?.sortMetric,
        [URL_ORDER]: e?.sortVal,
        [URL_RATIO_TYPE]: e?.ratioType,
      },
    });
  };
  const onTableSortChageEdition = (e: any) => {
    setEditionPageSortAttr({
      currentSortValue: e?.sortVal,
      currentSortMetric: e?.sortMetric,
      currentRatioType: e?.ratioType,
    });
    updateShortLinkPath({
      shortLinkQuery: {
        [URL_2_SORT_NAME]: e?.sortMetric,
        [URL_2_ORDER]: e?.sortVal,
        [URL_2_RATIO_TYPE]: e?.ratioType,
      },
    });
  };

  const onTableSortChageCountrie = (e: any) => {
    setCountriesPageSortAttr({
      currentSortValue: e?.sortVal,
      currentSortMetric: e?.sortMetric,
      currentRatioType: e?.ratioType,
    });
    updateShortLinkPath({
      shortLinkQuery: {
        [URL_SORT_NAME]: e?.sortMetric,
        [URL_ORDER]: e?.sortVal,
        [URL_RATIO_TYPE]: e?.ratioType,
      },
    });
  };
  return {
    onTableSortChageTrend,
    onTableSortChageEdition,
    onTableSortChageCountrie,
    updateMobileDetailShortLink,
    updatePageCfgFromShortLink,
    trendPageSortAttr,
    editionPageSortAttr,
    countriesPageSortAttr,
    setEditionPageSortAttr,
    setCountriesPageSortAttr,
  };
}
